import { useState, useEffect } from 'react';
import { useProject, useUpdateProject, useProjects, useCustomers } from '../../hooks/useProjects';
import { useAdminUsers } from '../../hooks/useAdmin';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';
import { PersistCurrencyAmountInput } from '../common/CurrencyRateInput';
import { DateField } from '../ui/DateField';

interface Props {
  projectId: number;
  onClose: () => void;
}

export default function ProjectEditModal({ projectId, onClose }: Props) {
  const { data: project, isLoading: projectLoading } = useProject(projectId);
  const { data: customers } = useCustomers();
  const { data: users } = useAdminUsers();
  const { data: allProjects } = useProjects();
  const updateMutation = useUpdateProject();

  const [form, setForm] = useState({
    name: '',
    fullName: '',
    description: '',
    parentId: '',
    customerId: '',
    projectManagerId: '',
    startDate: '',
    endDate: '',
    defaultRate: '',
    hoursTotal: '',
    moneyTotal: '',
    // Phase 72 (currency-aware budgets) — persisted alongside moneyTotal.
    moneyTotalCurrency: 'CZK' as 'CZK' | 'EUR' | 'USD',
    isDefault: false,
    mandatoryTasks: false,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (project) {
      setForm({
        name: project.name || '',
        fullName: project.fullName || '',
        description: project.description || '',
        parentId: project.parent?.id?.toString() || '',
        customerId: project.customer?.id?.toString() || '',
        projectManagerId: project.projectManager?.id?.toString() || '',
        startDate: project.startDate || '',
        endDate: project.endDate || '',
        defaultRate: project.defaultRate?.toString() || '',
        hoursTotal: project.hoursTotal?.toString() || '',
        moneyTotal: project.moneyTotal?.toString() || '',
        moneyTotalCurrency: (project.moneyTotalCurrency as 'CZK' | 'EUR' | 'USD') ?? 'CZK',
        isDefault: project.isDefault || false,
        mandatoryTasks: project.mandatoryTasks || false,
      });
    }
  }, [project]);

  const updateField = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Project name is required.');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: projectId,
        input: {
          name: form.name.trim(),
          fullName: form.fullName.trim() || undefined,
          description: form.description.trim() || undefined,
          parentId: form.parentId ? parseInt(form.parentId, 10) : undefined,
          customerId: form.customerId ? parseInt(form.customerId, 10) : undefined,
          projectManagerId: form.projectManagerId ? parseInt(form.projectManagerId, 10) : undefined,
          startDate: form.startDate || undefined,
          endDate: form.endDate || undefined,
          defaultRate: form.defaultRate ? parseFloat(form.defaultRate) : undefined,
          hoursTotal: form.hoursTotal ? parseFloat(form.hoursTotal) : undefined,
          moneyTotal: form.moneyTotal ? parseFloat(form.moneyTotal) : undefined,
          moneyTotalCurrency: form.moneyTotalCurrency,
          isDefault: form.isDefault,
          mandatoryTasks: form.mandatoryTasks,
        },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to save project.');
    }
  };

  const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <Modal isOpen onClose={onClose} title="Edit Project" size="lg">
      {projectLoading ? (
        <div className="flex items-center justify-center h-24"><Spinner /></div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
            <input type="text" value={form.name} onChange={(e) => updateField('name', e.target.value)} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input type="text" value={form.fullName} onChange={(e) => updateField('fullName', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => updateField('description', e.target.value)} rows={3} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Parent Project</label>
            <select value={form.parentId} onChange={(e) => updateField('parentId', e.target.value)} className={inputClass}>
              <option value="">No parent (top-level project)</option>
              {allProjects
                ?.filter((p) => p.id !== projectId && p.state !== 'archived' && p.state !== 'cancelled' && p.state !== 'completed')
                .sort((a, b) => (a.fullName || a.name).localeCompare(b.fullName || b.name))
                .map((p) => (
                <option key={p.id} value={String(p.id)}>{p.fullName || p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <select value={form.customerId} onChange={(e) => updateField('customerId', e.target.value)} className={inputClass}>
              <option value="">No customer</option>
              {customers?.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Manager</label>
            <select value={form.projectManagerId} onChange={(e) => updateField('projectManagerId', e.target.value)} className={inputClass}>
              <option value="">No project manager</option>
              {users?.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {[u.lastName, u.firstName].filter(Boolean).join(' ') || u.email}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <DateField value={form.startDate} onChange={(nextDate) => updateField('startDate', nextDate)} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <DateField value={form.endDate} onChange={(nextDate) => updateField('endDate', nextDate)} className={inputClass} />
            </div>
          </div>
          {/* Phase 72 (currency-aware budgets, Plan 04) — the "Default Rate"
              CurrencyRateInput was removed from the UI here for the same
              reason as the project form: its own currency selector collided
              visually with the new Budget currency selector. `form.defaultRate`
              stays in state and is still sent on submit so an existing
              project's stored value is preserved (non-destructive). */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget (Hours)</label>
              <input type="number" step="0.5" min="0" value={form.hoursTotal} onChange={(e) => updateField('hoursTotal', e.target.value)} className={inputClass} placeholder="0" />
            </div>
            {/* Phase 72 (currency-aware budgets, CUR-06) — Budget accepts
                CZK/EUR/USD and PERSISTS the entered currency (rather than
                converting-and-discarding it) with a "≈ X CZK (provisional)"
                preview. */}
            <PersistCurrencyAmountInput
              label="Budget"
              amount={form.moneyTotal}
              currency={form.moneyTotalCurrency}
              onAmountChange={(v) => updateField('moneyTotal', v)}
              onCurrencyChange={(c) => updateField('moneyTotalCurrency', c)}
              placeholder="0.00"
              amountInputId={`editMoneyTotal-${projectId}`}
              currencySelectId={`editMoneyTotalCurrency-${projectId}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id={`editIsDefault-${projectId}`} checked={form.isDefault} onChange={(e) => updateField('isDefault', e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <label htmlFor={`editIsDefault-${projectId}`} className="text-sm text-gray-700">Default project (visible to all users for time tracking)</label>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id={`editMandatoryTasks-${projectId}`} checked={form.mandatoryTasks} onChange={(e) => updateField('mandatoryTasks', e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <label htmlFor={`editMandatoryTasks-${projectId}`} className="text-sm text-gray-700">Mandatory tasks (require task selection for time entries)</label>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button type="submit" isLoading={updateMutation.isPending}>Save Changes</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
